import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Tooltip } from 'antd'
import { lazy, Suspense, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import type { ServerACLL3Rule } from '@/api/type-server'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Button } from '@/components/base/Button'
import { ButtonRadio } from '@/components/base/ButtonRadio'
import { Divider } from '@/components/base/Divider'
import { Loading } from '@/components/base/Loading'
import { Pagination } from '@/components/base/Pagination'
import { EmptyView } from '@/components/EmptyView'
import { Trans } from '@/components/Trans'
import {
  getEIPACLPath,
  getEIPL3RuleCreatePath,
  getEIPL3RuleEditPath,
  getEIPListPath,
  useUnifiedContext,
} from '@/contexts/UnifiedContext'
import { ACL_L3_RULE_LIMIT, ACLDirection, ACLL3RuleState } from '@/models/acl'
import { EIPBreadcrumb } from '@/pages/eip/components/EIPBreadcrumb'

import { L3RuleRow } from './components/L3RuleRow'

const L3RuleDeleteModal = lazy(() =>
  import('./components/L3RuleDeleteModal').then((m) => ({ default: m.L3RuleDeleteModal })),
)

const PAGE_SIZE = 10

export function ACLRulesPage() {
  const { t } = useTranslation('eip')
  const navigate = useNavigate()
  const ctx = useUnifiedContext()
  const queryClient = useQueryClient()
  const { eipResId } = useParams<{ eipResId: string }>()
  const eipId = eipResId ?? ''
  const eipListPath = getEIPListPath(ctx)
  const aclPath = getEIPACLPath(ctx, eipId)
  const [direction, setDirection] = useState<ACLDirection>(ACLDirection.Inbound)
  const [page, setPage] = useState(1)
  const [deleteL3Rule, setDeleteL3Rule] = useState<ServerACLL3Rule | undefined>()

  const goCreateL3 = () => {
    navigate(getEIPL3RuleCreatePath(ctx, eipId), {
      state: { defaultDirection: direction },
    })
  }
  const goEditL3 = (rule: ServerACLL3Rule) => {
    navigate(getEIPL3RuleEditPath(ctx, eipId, rule.id), { state: { rule } })
  }
  const goDuplicateL3 = (rule: ServerACLL3Rule) => {
    navigate(getEIPL3RuleCreatePath(ctx, eipId), {
      state: { defaultDirection: rule.direction, template: rule },
    })
  }

  const refetchL3 = () => {
    queryClient.invalidateQueries({ queryKey: ['aclL3Rules'] })
  }

  const { data: acl, isLoading: aclLoading } = useQuery({
    queryKey: ['eipACL', ctx.mode, ctx.teamResID, ctx.memberResID, eipResId],
    enabled: !!eipResId,
    queryFn: async ({ signal }) => {
      const res = await API.getACLResource({ net_res_id: eipResId! }, { signal })
      if (res.code !== 0) throw new Error(res.msg)
      return res.resource
    },
  })

  const aclResID = acl?.resID

  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['aclL3Rules', ctx.mode, ctx.teamResID, ctx.memberResID, aclResID, direction, page],
    enabled: !!aclResID,
    queryFn: async ({ signal }) => {
      const res = await API.getACLL3Rules(
        {
          acl_res_id: aclResID!,
          traffic_direction: direction,
          page,
          per_page: PAGE_SIZE,
        },
        { signal },
      )
      if (res.code !== 0) throw new Error(res.msg)
      return { rules: res.l3Rules ?? [], total: res.pagination?.total ?? 0 }
    },
    refetchInterval: (query) => {
      const rules = query.state.data?.rules ?? []
      return rules.some((r) => r.state?.status === ACLL3RuleState.Pending) ? 2000 : false
    },
  })

  const rules = rulesData?.rules ?? []
  const total = rulesData?.total ?? 0

  // L3 rule limit applies across both directions, so sum Inbound + Outbound totals.
  const { data: l3TotalAllDirections = 0 } = useQuery({
    queryKey: [
      'aclL3Rules',
      ctx.mode,
      ctx.teamResID,
      ctx.memberResID,
      aclResID,
      'totalAllDirections',
    ],
    enabled: !!aclResID,
    queryFn: async ({ signal }) => {
      const [inb, outb] = await Promise.all([
        API.getACLL3Rules(
          {
            acl_res_id: aclResID!,
            traffic_direction: ACLDirection.Inbound,
            page: 1,
            per_page: 1,
          },
          { signal },
        ),
        API.getACLL3Rules(
          {
            acl_res_id: aclResID!,
            traffic_direction: ACLDirection.Outbound,
            page: 1,
            per_page: 1,
          },
          { signal },
        ),
      ])
      if (inb.code !== 0) throw new Error(inb.msg)
      if (outb.code !== 0) throw new Error(outb.msg)
      return (inb.pagination?.total ?? 0) + (outb.pagination?.total ?? 0)
    },
  })
  const isL3LimitReached = l3TotalAllDirections >= ACL_L3_RULE_LIMIT

  const directionTabs = useMemo(
    () => [
      {
        label: (
          <Trans ns="eip" i18nKey="aclRules.directionInbound">
            Inbound
          </Trans>
        ),
        value: ACLDirection.Inbound,
      },
      {
        label: (
          <Trans ns="eip" i18nKey="aclRules.directionOutbound">
            Outbound
          </Trans>
        ),
        value: ACLDirection.Outbound,
      },
    ],
    [],
  )

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      {/* Header — back arrow + breadcrumb + Add Rule */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-tertiary cursor-pointer hover:text-primary"
            onClick={() => navigate(eipListPath)}
            aria-label="back"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <EIPBreadcrumb
            items={[
              {
                label: (
                  <Trans ns="eip" i18nKey="pageTitle">
                    Access Point
                  </Trans>
                ),
                to: eipListPath,
              },
              {
                label: acl?.resID ?? eipResId,
                to: aclPath,
                active: true,
              },
            ]}
          />
        </div>
        <Tooltip
          title={
            isL3LimitReached ? (
              <span className="t-sm">{t('aclRules.l3LimitReachedTooltip')}</span>
            ) : null
          }
          mouseEnterDelay={0}
        >
          <span className={isL3LimitReached ? 'inline-block cursor-not-allowed' : undefined}>
            <Button
              type="secondary"
              className="min-w-[88px]"
              onClick={goCreateL3}
              disabled={!aclResID || isL3LimitReached}
            >
              <Trans ns="eip" i18nKey="aclRules.addRule">
                Add Rule
              </Trans>
            </Button>
          </span>
        </Tooltip>
      </div>

      <Divider />

      <div className="mt-4">
        <ButtonRadio
          value={direction}
          items={directionTabs}
          onChange={(value) => {
            setDirection(value)
            setPage(1)
          }}
        />
      </div>

      {aclLoading || rulesLoading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : rules.length > 0 ? (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={3}>
                <Trans ns="eip" i18nKey="aclRules.colRemoteCIDR">
                  Remote CIDR
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans ns="eip" i18nKey="aclRules.colDirection">
                  Direction
                </Trans>
              </th>
              <th colSpan={3}>
                <Trans ns="eip" i18nKey="aclRules.colDefaultPolicy">
                  Default Policy
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans ns="eip" i18nKey="aclRules.colStatus">
                  Status
                </Trans>
              </th>
              <th colSpan={6}>
                <Trans ns="eip" i18nKey="aclRules.colDescription">
                  Description
                </Trans>
              </th>
              <th colSpan={5}>
                <Trans ns="eip" i18nKey="aclRules.colCreatedTime">
                  Created Time
                </Trans>
              </th>
              <th colSpan={2}>
                <div className="text-right">
                  <Trans ns="eip" i18nKey="aclRules.colAction">
                    Action
                  </Trans>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <L3RuleRow
                key={rule.id}
                rule={rule}
                onEdit={goEditL3}
                onDuplicate={goDuplicateL3}
                onDelete={setDeleteL3Rule}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <div className="mt-4 mb-2">
          <EmptyView
            content={
              <Trans ns="eip" i18nKey="aclRules.empty">
                No ACL rules.
              </Trans>
            }
          />
        </div>
      )}

      {total > 0 && !rulesLoading && (
        <Pagination
          size={PAGE_SIZE}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={setPage}
        />
      )}

      <Suspense>
        {deleteL3Rule && aclResID && (
          <L3RuleDeleteModal
            aclResID={aclResID}
            rule={deleteL3Rule}
            onSuccess={() => {
              setDeleteL3Rule(undefined)
              refetchL3()
            }}
            onClose={() => setDeleteL3Rule(undefined)}
          />
        )}
      </Suspense>
    </div>
  )
}
